import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Search, FileCheck2, CalendarClock, MessageSquare, FolderOpen, ShieldCheck, ListChecks } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PlanCards } from "@/components/PlanCards";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/api";

const IMG = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400`;
const PREVIEW = [
  { name: "Priya Sharma", role: "Operations Lead", rate: 32, img: IMG("photo-1749700332031-cf99864959ea"), skills: ["Executive Ops", "HubSpot"] },
  { name: "Marcus Vance", role: "Executive Assistant", rate: 28, img: IMG("photo-1611679782010-5ac7ff596d9a"), skills: ["Calendar", "QuickBooks"] },
  { name: "Chloe Lin", role: "Customer Success", rate: 18, img: IMG("photo-1484863137850-59afcfe05386"), skills: ["Zendesk", "Shopify"] },
];

const STEPS = [
  { n: "01", t: "Choose a membership", d: "Pick a plan that matches how many virtual workers you need." },
  { n: "02", t: "Browse vetted talent", d: "Filter candidates by role, skills, rate, timezone and experience." },
  { n: "03", t: "Interview & select", d: "Request interviews with your shortlist. We handle the scheduling." },
  { n: "04", t: "Hire & onboard", d: "Send a hire request. We manage contracts, payroll and onboarding." },
];

const FEATURES = [
  { icon: Search, t: "Searchable talent directory", d: "Every candidate pre-screened by our recruiters.", span: "lg:col-span-7" },
  { icon: FileCheck2, t: "Structured profiles", d: "Rates, experience, tools, work history and education at a glance.", span: "lg:col-span-5" },
  { icon: CalendarClock, t: "Interview scheduling", d: "Propose times. We confirm and send the link.", span: "lg:col-span-4" },
  { icon: MessageSquare, t: "Direct line to our team", d: "Message your recruitment team any time.", span: "lg:col-span-4" },
  { icon: FolderOpen, t: "Business file library", d: "Templates, SOPs and HR docs included.", span: "lg:col-span-4" },
];

const FAQ = [
  ["What does the membership include?", "Access to our full vetted candidate directory, interview and hire requests, messaging with our recruitment team and our library of business templates."],
  ["Are workers' wages included in the fee?", "No. The membership gives you access to our platform and recruitment service. Each worker's hourly rate is shown on their profile and billed separately once hired."],
  ["How fast can I hire?", "Most clients interview within 48 hours and have a worker started within a week."],
  ["Can I cancel anytime?", "Yes. Memberships are billed monthly and can be cancelled any time."],
];

const Nav = () => {
  const { user } = useAuth();
  const home = user ? ({ admin: "/admin", candidate: "/candidate" }[user.role] || "/app") : null;
  return (
    <header className="sticky top-0 z-50 border-b border-stone-200/80 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600">
          <a href="#how" className="hover:text-stone-900 transition-colors" data-testid="nav-link-how">How it works</a>
          <a href="#features" className="hover:text-stone-900 transition-colors" data-testid="nav-link-features">Features</a>
          <a href="#pricing" className="hover:text-stone-900 transition-colors" data-testid="nav-link-pricing">Pricing</a>
          <a href="#faq" className="hover:text-stone-900 transition-colors" data-testid="nav-link-faq">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          {home ? (
            <Link to={home} data-testid="nav-dashboard-button" className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 transition-colors">Open portal</Link>
          ) : (
            <>
              <Link to="/apply" data-testid="nav-apply-link" className="hidden sm:inline text-sm text-stone-700 hover:text-stone-900">Apply as candidate</Link>
              <Link to="/login" data-testid="nav-login-button" className="text-sm text-stone-700 hover:text-stone-900">Sign in</Link>
              <Link to="/register" data-testid="nav-register-button" className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 transition-colors">Get started</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const HeroPreview = () => (
  <div className="relative mx-auto w-full max-w-md lg:ml-auto">
    {PREVIEW.map((c, i) => (
      <div key={c.name} className={`rise d${i + 2} relative mb-4 flex items-center gap-4 rounded-md border border-stone-200 bg-white p-4 shadow-[0_18px_40px_-28px_rgba(15,23,42,.45)] ${i === 1 ? "lg:-ml-10" : i === 2 ? "lg:ml-8" : ""}`}>
        <img src={c.img} alt={c.name} className="h-14 w-14 rounded-md object-cover" />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-stone-900">{c.name}</p>
          <p className="text-xs text-stone-500">{c.role}</p>
          <div className="mt-1.5 flex gap-1">{c.skills.map((s) => <span key={s} className="rounded-sm bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-600">{s}</span>)}</div>
        </div>
        <div className="text-right">
          <p className="font-display text-lg font-extrabold text-stone-900">${c.rate}</p>
          <p className="text-[10px] font-mono uppercase text-stone-500">per hr</p>
        </div>
      </div>
    ))}
    <div className="rise d4 absolute -bottom-6 -left-4 hidden sm:flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs text-white">
      <ShieldCheck className="h-4 w-4 text-amber-400" /> Every candidate recruiter-vetted
    </div>
  </div>
);

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const onPlan = async (p) => {
    if (!user) return nav(`/register?plan=${p.id}`);
    if (user.role === "admin") return nav("/admin");
    await startCheckout(p.id);
  };

  return (
    <div className="bg-[#FBFBF9]" data-testid="landing-page">
      <Nav />

      <section className="relative overflow-hidden grain">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-7">
            <p className="rise text-xs font-mono uppercase tracking-[0.2em] text-amber-700">Virtual workforce · Membership</p>
            <h1 className="rise d1 mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04] tracking-tight text-stone-900">
              Hand-pick your next virtual worker. <span className="text-stone-400">We handle the rest.</span>
            </h1>
            <p className="rise d2 mt-6 max-w-xl text-base sm:text-lg text-stone-600 leading-relaxed">
              Browse recruiter-vetted virtual assistants, developers, support and sales talent. Compare structured profiles, book interviews and hire — all from one member portal.
            </p>
            <div className="rise d3 mt-10 flex flex-wrap items-center gap-4">
              <Link to={user ? (user.role === "admin" ? "/admin" : "/app/candidates") : "/register"} data-testid="hero-cta-button" className="group inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-3.5 text-sm font-medium text-white hover:bg-slate-700 transition-colors">
                Browse candidates <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href="#pricing" data-testid="hero-pricing-link" className="rounded-md border border-stone-300 px-6 py-3.5 text-sm font-medium text-stone-900 hover:border-stone-900 transition-colors">See membership plans</a>
            </div>
            <dl className="rise d4 mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-stone-200 pt-8">
              {[["48h", "Avg. time to interview"], ["Top 3%", "Applicants accepted"], ["24/7", "Coverage across timezones"]].map(([v, l]) => (
                <div key={l}><dt className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900">{v}</dt><dd className="mt-1 text-xs text-stone-500">{l}</dd></div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5"><HeroPreview /></div>
        </div>
      </section>

      <section id="how" className="border-y border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-700">How it works</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">From membership to a working team in four steps.</h2>
          <div className="mt-14 grid gap-px overflow-hidden rounded-md border border-stone-200 bg-stone-200 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white p-8">
                <p className="font-mono text-sm text-amber-700">{s.n}</p>
                <p className="mt-6 font-display text-xl font-bold text-stone-900">{s.t}</p>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-700">Included with membership</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">Everything you need to hire remote talent with confidence.</h2>
            <div className="mt-8 overflow-hidden rounded-md">
              <img src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900" alt="Our recruitment team" className="h-64 w-full object-cover" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-12">
            {FEATURES.map((f) => (
              <div key={f.t} className={`lift rounded-md border border-stone-200 bg-white p-8 ${f.span}`}>
                <f.icon className="h-6 w-6 text-slate-900" strokeWidth={1.5} />
                <p className="mt-10 font-display text-xl font-bold text-stone-900">{f.t}</p>
                <p className="mt-2 text-sm text-stone-600">{f.d}</p>
              </div>
            ))}
            <div className="flex flex-col justify-between rounded-md bg-slate-900 p-8 text-white sm:col-span-2 lg:col-span-12 lg:flex-row lg:items-center">
              <div className="flex items-center gap-4"><ListChecks className="h-6 w-6 text-amber-400" /><p className="font-display text-xl font-bold">Shortlist, compare and decide — side by side.</p></div>
              <a href="#pricing" className="mt-4 text-sm text-amber-400 underline underline-offset-4 lg:mt-0">Start your membership</a>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="border-y border-stone-200 bg-[#F5F5F0]">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-700">Membership</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">Simple monthly plans.</h2>
          <p className="mt-3 max-w-xl text-stone-600">Your membership unlocks the portal and our recruitment service. Worker rates are billed separately once hired.</p>
          <div className="mt-14"><PlanCards onSelect={onPlan} /></div>
        </div>
      </section>

      <section id="faq" className="mx-auto max-w-3xl px-5 py-24 sm:px-8">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">Questions, answered.</h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}>
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg font-bold">{q}</AccordionTrigger>
              <AccordionContent className="text-stone-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <footer className="bg-slate-900 text-stone-400">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Logo light />
          <p className="text-sm">© {new Date().getFullYear()} VirtuForce. Recruitment & virtual workforce solutions.</p>
        </div>
      </footer>
    </div>
  );
}
